import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemText,
  FormControl,
  Select,
  SelectChangeEvent,
  styled,
  ListSubheader,
  IconButton,
} from '@mui/material';
import {
  ChartBar,
  Buildings,
  CreditCard,
  CheckCircle,
  EyeIcon,
  EyeSlashIcon,
} from '@phosphor-icons/react';

// Static POS options (API will be wired later)
const POS_OPTIONS = [{ posName: 'Urban Piper' }, { posName: 'tmbill' }];

// Reservation systems for HMS (mirrors ReservationSetup)
const RESERVATION_SYSTEMS = {
  hotels: [{ id: 'hotelogix', name: 'Hotelogix' }],
  restaurants: [{ id: 'reservego', name: 'ReserveGo' }],
};

export interface HotelConfig {
  hotelId: string;
  hotelName?: string;
  apiUsername: string;
  counterId: string;
  consumerKey: string;
  consumerSecret: string;
  baseUrl: string;
}

export interface HMSIntegrationConfig {
  selectedSystem: string;
  restaurantIds: Record<string, string>;
  hotelConfigs: Record<string, HotelConfig>;
}

const DEFAULT_HMS_CONFIG: HMSIntegrationConfig = {
  selectedSystem: '',
  restaurantIds: {},
  hotelConfigs: {},
};

const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#B2B7C2',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2563EB',
  },
});

const StyledFormControl = styled(FormControl)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '50%',
});

const URBAN_PIPER_ENVIRONMENTS = ['stage', 'prod'];

// CRM options (match ConnectCrmModal)
const CRM_OPTIONS = [{ value: 'hubspot', label: 'HubSpot' }] as const;

export interface CRMIntegrationConfig {
  crmProvider: '' | 'hubspot';
  apiKey: string;
}

const DEFAULT_CRM_CONFIG: CRMIntegrationConfig = {
  crmProvider: '',
  apiKey: '',
};

export interface POSIntegrationConfig {
  posName?: string;
  environment?: string;
  businessId?: string;
  isAccessRequested?: boolean;
  tmbillposId?: string;
  menuSettingDetails?: Record<
    string,
    { storeId: string; refId?: string; tmBillBrandShortName?: string }
  >;
}

interface IntegrationSelectionProps {
  selectedType: 'crm' | 'hms' | 'pos' | null;
  config: any;
  onIntegrationChange: (
    type: 'crm' | 'hms' | 'pos' | null,
    config: any
  ) => void;
  onValidationChange: (isValid: boolean) => void;
  /** Stores from business setup (for POS Location ID / Brand Short Name per store) */
  stores?: { id?: string; storeId?: string; friendly_name?: string }[];
}

const IntegrationSelection = ({
  selectedType,
  config,
  onIntegrationChange,
  onValidationChange,
  stores = [],
}: IntegrationSelectionProps) => {
  useEffect(() => {
    // This step is optional, so it's always valid
    onValidationChange(true);
  }, [onValidationChange]);

  const posConfig = config as POSIntegrationConfig;
  const menuSettingDetails = posConfig?.menuSettingDetails || {};

  const handlePosFieldChange = (field: string, value: string | boolean) => {
    onIntegrationChange('pos', { ...posConfig, [field]: value });
  };

  const handleMenuSettingDetail = (
    storeId: string,
    key: 'refId' | 'tmBillBrandShortName',
    value: string
  ) => {
    const current = menuSettingDetails[storeId] || { storeId };
    onIntegrationChange('pos', {
      ...posConfig,
      menuSettingDetails: {
        ...menuSettingDetails,
        [storeId]: { ...current, [key]: value },
      },
    });
  };

  const handleRequestAccess = () => {
    handlePosFieldChange('isAccessRequested', true);
  };

  // HMS: config and local state (no Redux/APIs)
  const hmsConfig: HMSIntegrationConfig =
    selectedType === 'hms'
      ? {
          selectedSystem:
            (config as HMSIntegrationConfig)?.selectedSystem ?? '',
          restaurantIds: (config as HMSIntegrationConfig)?.restaurantIds ?? {},
          hotelConfigs: (config as HMSIntegrationConfig)?.hotelConfigs ?? {},
        }
      : DEFAULT_HMS_CONFIG;

  const [showPasswords, setShowPasswords] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const handleHmsSystemChange = (value: string) => {
    onIntegrationChange('hms', { ...hmsConfig, selectedSystem: value });
  };

  const handleHmsRestaurantIdChange = (storeId: string, value: string) => {
    onIntegrationChange('hms', {
      ...hmsConfig,
      restaurantIds: { ...hmsConfig.restaurantIds, [storeId]: value },
    });
  };

  const handleHmsHotelConfigChange = (
    storeId: string,
    field: keyof HotelConfig,
    value: string
  ) => {
    const existing = hmsConfig.hotelConfigs[storeId] || {
      hotelId: '',
      hotelName: '',
      apiUsername: '',
      counterId: '',
      consumerKey: '',
      consumerSecret: '',
      baseUrl: 'https://crs.hotelogix.net/ws/web/',
    };
    onIntegrationChange('hms', {
      ...hmsConfig,
      hotelConfigs: {
        ...hmsConfig.hotelConfigs,
        [storeId]: { ...existing, [field]: value },
      },
    });
  };

  const toggleHmsPasswordVisibility = (storeId: string, field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [storeId]: {
        ...prev[storeId],
        [field]: !prev[storeId]?.[field],
      },
    }));
  };

  // CRM: config and handlers (no API in wizard)
  const crmConfig: CRMIntegrationConfig =
    selectedType === 'crm'
      ? {
          crmProvider: (config as CRMIntegrationConfig)?.crmProvider ?? '',
          apiKey: (config as CRMIntegrationConfig)?.apiKey ?? '',
        }
      : DEFAULT_CRM_CONFIG;

  const handleCrmProviderChange = (value: '' | 'hubspot') => {
    onIntegrationChange('crm', {
      ...crmConfig,
      crmProvider: value,
      apiKey: value ? crmConfig.apiKey : '',
    });
  };

  const handleCrmApiKeyChange = (value: string) => {
    onIntegrationChange('crm', { ...crmConfig, apiKey: value });
  };

  const renderHmsHotelConfigFields = (store: {
    id?: string;
    storeId?: string;
    friendly_name?: string;
  }) => {
    const storeId = String(store.id ?? store.storeId ?? '');
    const hotelConfig = hmsConfig.hotelConfigs[storeId] || {
      hotelId: '',
      hotelName: '',
      apiUsername: '',
      counterId: '',
      consumerKey: '',
      consumerSecret: '',
      baseUrl: 'https://crs.hotelogix.net/ws/web/',
    };

    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, fontSize: '14px' }}
        >
          Hotel Configuration for {store.friendly_name ?? storeId}
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, fontWeight: 600, fontSize: '12px' }}
          >
            Basic Configuration
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Hotel ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Hotel ID (e.g., 14095)"
              size="small"
              value={hotelConfig.hotelId}
              onChange={e =>
                handleHmsHotelConfigChange(storeId, 'hotelId', e.target.value)
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': { height: '40px' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Hotel Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Hotel Name"
              size="small"
              value={hotelConfig.hotelName || ''}
              onChange={e =>
                handleHmsHotelConfigChange(storeId, 'hotelName', e.target.value)
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': { height: '40px' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              API Username
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter API Username"
              size="small"
              value={hotelConfig.apiUsername}
              onChange={e =>
                handleHmsHotelConfigChange(
                  storeId,
                  'apiUsername',
                  e.target.value
                )
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': { height: '40px' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Counter ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Counter ID"
              size="small"
              value={hotelConfig.counterId}
              onChange={e =>
                handleHmsHotelConfigChange(storeId, 'counterId', e.target.value)
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': { height: '40px' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Consumer Key
            </Typography>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Enter Consumer Key"
                size="small"
                type={showPasswords[storeId]?.consumerKey ? 'text' : 'password'}
                value={hotelConfig.consumerKey}
                onChange={e =>
                  handleHmsHotelConfigChange(
                    storeId,
                    'consumerKey',
                    e.target.value
                  )
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '12px',
                  },
                  '& > div': { height: '40px' },
                }}
              />
              <IconButton
                onClick={() =>
                  toggleHmsPasswordVisibility(storeId, 'consumerKey')
                }
                size="small"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                }}
              >
                {showPasswords[storeId]?.consumerKey ? (
                  <EyeSlashIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Consumer Secret
            </Typography>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Enter Consumer Secret"
                size="small"
                type={
                  showPasswords[storeId]?.consumerSecret ? 'text' : 'password'
                }
                value={hotelConfig.consumerSecret}
                onChange={e =>
                  handleHmsHotelConfigChange(
                    storeId,
                    'consumerSecret',
                    e.target.value
                  )
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '12px',
                  },
                  '& > div': { height: '40px' },
                }}
              />
              <IconButton
                onClick={() =>
                  toggleHmsPasswordVisibility(storeId, 'consumerSecret')
                }
                size="small"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                }}
              >
                {showPasswords[storeId]?.consumerSecret ? (
                  <EyeSlashIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Base URL
            </Typography>
            <TextField
              fullWidth
              placeholder="https://crs.hotelogix.net/ws/web/"
              size="small"
              value={hotelConfig.baseUrl}
              onChange={e =>
                handleHmsHotelConfigChange(storeId, 'baseUrl', e.target.value)
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': { height: '40px' },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const integrations = [
    {
      type: 'crm' as const,
      icon: ChartBar,
      title: 'CRM',
      providers: 'Salesforce, HubSpot, Zoho',
      color: '#FF6B6B',
    },
    {
      type: 'hms' as const,
      icon: Buildings,
      title: 'HMS',
      providers: 'Opera, Mews, Cloudbeds',
      color: '#4ECDC4',
    },
    {
      type: 'pos' as const,
      icon: CreditCard,
      title: 'POS',
      providers: 'Square, Toast, Clover',
      color: '#FFA500',
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: '1000px',
        margin: '0 auto',
        py: 4,
      }}
    >
      {/* Heading */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: 'custom.textBlue',
          fontSize: '18px',
          mb: 1,
          textAlign: 'center',
        }}
      >
        Integrate with your existing CRM, Hotel Management System, or POS to
        sync data
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        {integrations.map(integration => {
          const isSelected = selectedType === integration.type;
          const Icon = integration.icon;

          return (
            <Box
              key={integration.type}
              onClick={() => {
                if (integration.type === 'pos') {
                  onIntegrationChange(integration.type, config || {});
                } else if (integration.type === 'hms') {
                  const hasHmsConfig =
                    config &&
                    (config as HMSIntegrationConfig).selectedSystem !==
                      undefined;
                  onIntegrationChange(
                    'hms',
                    hasHmsConfig ? config : { ...DEFAULT_HMS_CONFIG }
                  );
                } else if (integration.type === 'crm') {
                  const hasCrmConfig =
                    config &&
                    (config as CRMIntegrationConfig).crmProvider !== undefined;
                  onIntegrationChange(
                    'crm',
                    hasCrmConfig ? config : { ...DEFAULT_CRM_CONFIG }
                  );
                }
              }}
              sx={{
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: isSelected ? 'primary.main' : 'lightGray.main',
                borderRadius: '12px',
                p: 3,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                minHeight: '180px',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  borderColor: isSelected
                    ? 'primary.main'
                    : 'custom.secondaryBlue',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(90, 76, 230, 0.1)',
                },
              }}
            >
              {/* Selected indicator */}
              {isSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ color: 'white', fontSize: '14px' }}>
                    ✓
                  </Typography>
                </Box>
              )}

              {/* Icon */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  bgcolor: `${integration.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Icon size={28} color={integration.color} weight="duotone" />
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'custom.textBlue',
                  mb: 1,
                }}
              >
                {integration.title}
              </Typography>

              {/* Providers */}
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  lineHeight: 1.4,
                }}
              >
                {integration.providers}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* POS integration form (same UI as MenuConfiguration – no API calls) */}
      {selectedType === 'pos' && (
        <Box
          sx={{
            display: 'flex',
            gap: '30px',
            justifyContent: 'center',
            flexDirection: 'row',
            alignItems: 'flex-start',
            mt: 2,
          }}
        >
          <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                borderRadius: '8px',
                p: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Select your POS
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={posConfig?.posName || ''}
                  onChange={e =>
                    handlePosFieldChange('posName', e.target.value)
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      height: '40px',
                    },
                    '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                  }}
                >
                  {POS_OPTIONS.map(pos => (
                    <MenuItem key={pos.posName} value={pos.posName}>
                      {pos.posName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {posConfig?.posName === 'Urban Piper' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: '120px', flexShrink: 0 }}
                    >
                      Environment
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={posConfig?.environment || 'stage'}
                      onChange={e =>
                        handlePosFieldChange('environment', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '6px' },
                      }}
                    >
                      {URBAN_PIPER_ENVIRONMENTS.map(env => (
                        <MenuItem key={env} value={env}>
                          {env.charAt(0).toUpperCase() + env.slice(1)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: '120px', flexShrink: 0 }}
                    >
                      Business ID
                    </Typography>
                    <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        placeholder="Enter your POS Business ID"
                        size="small"
                        value={posConfig?.businessId || ''}
                        onChange={e =>
                          handlePosFieldChange('businessId', e.target.value)
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '12px',
                          },
                          '& > div': { height: '40px' },
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={!posConfig?.businessId?.trim()}
                        onClick={handleRequestAccess}
                        sx={{
                          minWidth: 136,
                          height: 40,
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontSize: '14px',
                          boxShadow: 'none',
                          whiteSpace: 'nowrap',
                          ...(posConfig?.isAccessRequested && {
                            bgcolor: 'white',
                            border: '1px solid',
                            borderColor: 'rgba(55, 183, 125, 1)',
                            color: 'rgba(55, 183, 125, 1)',
                            pointerEvents: 'none',
                            '&:hover': { bgcolor: 'white' },
                          }),
                        }}
                      >
                        {posConfig?.isAccessRequested ? (
                          <>
                            <CheckCircle
                              size={16}
                              weight="fill"
                              color="rgba(55, 183, 125, 1)"
                              style={{ marginRight: 2 }}
                            />
                            Request Sent
                          </>
                        ) : (
                          'Request Access'
                        )}
                      </Button>
                    </Box>
                  </Box>
                  {posConfig?.isAccessRequested && stores.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ width: '140px' }}
                      >
                        Location ID
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          flex: 1,
                        }}
                      >
                        {stores.map((store: any) => {
                          const storeId = store.id ?? store.storeId ?? '';
                          if (!storeId) return null;
                          return (
                            <Box key={storeId} sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                placeholder="Store Alias"
                                size="small"
                                value={store.friendly_name || ''}
                                disabled
                                sx={{
                                  width: '200px',
                                  fontSize: '12px',
                                  bgcolor: 'rgb(243, 244, 246)',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                  },
                                }}
                              />
                              <TextField
                                placeholder="ref ID"
                                size="small"
                                value={menuSettingDetails[storeId]?.refId || ''}
                                onChange={e =>
                                  handleMenuSettingDetail(
                                    storeId,
                                    'refId',
                                    e.target.value
                                  )
                                }
                                sx={{
                                  width: '200px',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {posConfig?.posName === 'tmbill' && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      TM POS ID
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter your TM POS ID"
                      size="small"
                      value={posConfig?.tmbillposId || ''}
                      onChange={e =>
                        handlePosFieldChange('tmbillposId', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          height: '40px',
                        },
                        '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                      }}
                    />
                  </Box>
                  {posConfig?.tmbillposId?.trim() !== '' &&
                    stores.length > 0 && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Brand Short Name
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '12px', mb: 2 }}
                        >
                          Enter Brand Short Name for the respective store.
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {stores.map((store: any) => {
                            const storeId = store.id ?? store.storeId ?? '';
                            if (!storeId) return null;
                            return (
                              <Box
                                key={storeId}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                                }}
                              >
                                <TextField
                                  placeholder="Store Alias"
                                  size="small"
                                  value={store?.friendly_name || ''}
                                  disabled
                                  sx={{
                                    width: '35%',
                                    flexShrink: 0,
                                    bgcolor: 'rgb(243, 244, 246)',
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '6px',
                                      height: '40px',
                                    },
                                  }}
                                />
                                <TextField
                                  placeholder="brand short name"
                                  size="small"
                                  value={
                                    menuSettingDetails[storeId]
                                      ?.tmBillBrandShortName || ''
                                  }
                                  onChange={e =>
                                    handleMenuSettingDetail(
                                      storeId,
                                      'tmBillBrandShortName',
                                      e.target.value
                                    )
                                  }
                                  sx={{
                                    width: '40%',
                                    flexShrink: 0,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '6px',
                                      height: '40px',
                                    },
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ width: '40%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'lightGray.main',
                borderRadius: '8px',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
              }}
            >
              <Typography
                color="text.primary"
                fontWeight="500"
                sx={{ fontSize: '16px', mb: 1 }}
              >
                How to Connect Your POS with Quibble AI
              </Typography>
              <List sx={{ pt: 0, px: 0 }}>
                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText
                    primary="1. Enter Your Business ID"
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                    secondary="Provide your unique Business ID to initiate the POS integration."
                    secondaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '12px',
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText
                    primary="2. Request Integration Access"
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                    secondary={
                      'Click on "Request Access" to initiate the integration approval process with your POS system.'
                    }
                    secondaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '12px',
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText
                    primary="3. Enter Your Location ID"
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                    secondary="Use the Location ID from POS to accurately link your business location."
                    secondaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '12px',
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText
                    primary="4. Proceed to the Next Step"
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                    secondary="While your POS integration request is under review, you can continue setting up your AI assistant."
                    secondaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '12px',
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText
                    primary="5. Publish Your Menu"
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                    secondary={
                      'Once integration is approved, go to POS, select the desired locations, and click the "Publish" button to make your menu live.'
                    }
                    secondaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '12px',
                    }}
                  />
                </ListItem>
              </List>
            </Box>
          </Box>
        </Box>
      )}

      {/* HMS integration form (same UI as ReservationSetup) */}
      {selectedType === 'hms' && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <StyledFormControl>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#6B6F80',
                  maxWidth: '200px',
                  flexShrink: 0,
                }}
              >
                Select your Reservation Management System
              </Typography>
              <StyledSelect
                value={hmsConfig.selectedSystem}
                onChange={(event: SelectChangeEvent<unknown>) => {
                  const value =
                    typeof event.target.value === 'string'
                      ? event.target.value
                      : '';
                  handleHmsSystemChange(value);
                }}
                displayEmpty
                sx={{ flex: 1 }}
              >
                <MenuItem disabled value="">
                  <div style={{ color: '#6B6F80' }}>
                    Reservation Management System
                  </div>
                </MenuItem>
                <ListSubheader>Hotels</ListSubheader>
                {RESERVATION_SYSTEMS.hotels.map(system => (
                  <MenuItem
                    key={system.id}
                    value={system.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span>{system.name}</span>
                  </MenuItem>
                ))}
                <ListSubheader>Restaurants</ListSubheader>
                {RESERVATION_SYSTEMS.restaurants.map(system => (
                  <MenuItem
                    key={system.id}
                    value={system.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span>{system.name}</span>
                  </MenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>

            {hmsConfig.selectedSystem && (
              <Box sx={{ pt: 3 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, minWidth: '190px', flexShrink: 0 }}
                  >
                    {hmsConfig.selectedSystem === 'reservego'
                      ? 'Restaurant ID'
                      : 'Hotel Code'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '12px', mb: 2 }}
                  >
                    {hmsConfig.selectedSystem === 'reservego'
                      ? `You can find your Restaurant ID under Settings > API Integration`
                      : `You can find your Hotel Code in the Hotelogix Account`}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                    flexShrink: 0,
                  }}
                >
                  {stores.map(
                    (store: {
                      id?: string;
                      storeId?: string;
                      friendly_name?: string;
                    }) => {
                      const storeId = store.id ?? store.storeId ?? '';
                      if (!storeId) return null;
                      return (
                        <Box key={storeId}>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              flexWrap: { xs: 'wrap', md: 'nowrap' },
                            }}
                          >
                            <TextField
                              placeholder="Store Alias"
                              size="small"
                              value={store?.friendly_name ?? ''}
                              disabled
                              sx={{
                                width: '50%',
                                flexShrink: 0,
                                fontSize: '12px',
                                bgcolor: 'white',
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '6px',
                                  height: '40px',
                                  '& input': {
                                    fontSize: '14px',
                                    padding: '8px 12px',
                                  },
                                },
                              }}
                            />
                            <TextField
                              placeholder={
                                hmsConfig.selectedSystem === 'reservego'
                                  ? 'Restaurant ID'
                                  : 'Hotel Code'
                              }
                              size="small"
                              value={hmsConfig.restaurantIds[storeId] || ''}
                              onChange={e =>
                                handleHmsRestaurantIdChange(
                                  storeId,
                                  e.target.value
                                )
                              }
                              sx={{
                                width: '48%',
                                flexShrink: 0,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '6px',
                                  height: '40px',
                                  '& input': {
                                    fontSize: '14px',
                                    padding: '8px 12px',
                                  },
                                },
                              }}
                            />
                          </Box>
                          {hmsConfig.selectedSystem === 'hotelogix' &&
                            hmsConfig.restaurantIds[storeId] &&
                            renderHmsHotelConfigFields(store)}
                        </Box>
                      );
                    }
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* CRM integration form (inline, same pattern as POS/HMS) */}
      {selectedType === 'crm' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your CRM to sync contacts with Quibble AI.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
              >
                CRM
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={crmConfig.crmProvider}
                onChange={e =>
                  handleCrmProviderChange(
                    (e.target.value || '') as '' | 'hubspot'
                  )
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    height: '40px',
                  },
                  '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                }}
              >
                <MenuItem value="">
                  <span style={{ color: '#6B6F80' }}>Select a CRM</span>
                </MenuItem>
                {CRM_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {crmConfig.crmProvider === 'hubspot' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
                >
                  HubSpot API Key
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your HubSpot API key"
                  size="small"
                  value={crmConfig.apiKey}
                  onChange={e => handleCrmApiKeyChange(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      fontSize: '12px',
                    },
                    '& > div': { height: '40px' },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default IntegrationSelection;
